import { TranscriptElement } from '../../types.ts/transcript';

/**
 * Hook to manage transcript element status
 * This allows for a consistent approach to determining 
 * element status across components
 */
export default function useTranscriptStatus() {
  /**
   * Check if a word is in the active transcript
   * @param element Element to check
   * @param activeTranscript The current active transcript
   * @returns Boolean indicating if the element is in active transcript
   */
  const isElementActive = (
    element: TranscriptElement,
    activeTranscript?: TranscriptElement[]
  ): boolean => {
    if (!activeTranscript) return false;
    
    return activeTranscript.some(
      (e) =>
        e.type === 'text' &&
        element.type === 'text' &&
        e.word_index !== undefined &&
        element.word_index !== undefined &&
        e.word_index === element.word_index,
    );
  };

  /**
   * Check if a word can be removed (is in active transcript)
   * @param wordId ID of the word to check
   * @param activeTranscript The current active transcript
   * @param viewMode The current view mode ('active', 'original', or 'full')
   * @returns Boolean indicating if the word can be removed
   */
  const canWordBeRemoved = (
    wordId: string,
    activeTranscript?: TranscriptElement[],
    viewMode: 'active' | 'original' | 'full' = 'active'
  ): boolean => {
    if (!activeTranscript) return false;
    
    // In active view, all words are removable
    if (viewMode === 'active') return true;
    
    // In original view, only words that are in active transcript can be removed
    return activeTranscript.some(
      (word) =>
        word.type === 'text' &&
        word.word_index !== undefined &&
        word.word_index.toString() === wordId
    );
  };

  /**
   * Check if a word can be restored (is in original, not in active)
   * @param wordId ID of the word to check
   * @param originalTranscript The original transcript
   * @param activeTranscript The current active transcript
   * @param viewMode The current view mode ('active', 'original', or 'full')
   * @returns Boolean indicating if the word can be restored
   */
  const canWordBeRestored = (
    wordId: string,
    originalTranscript?: ReadonlyArray<TranscriptElement>,
    activeTranscript?: TranscriptElement[],
    viewMode: 'active' | 'original' | 'full' = 'original'
  ): boolean => {
    if (!originalTranscript || !activeTranscript) return false;
    
    // Only allow restore in original or full view mode
    if (viewMode !== 'original' && viewMode !== 'full') return false;
    
    // Check if word is in original transcript
    const isInOriginal = originalTranscript.some(
      (word) =>
        word.type === 'text' &&
        word.word_index !== undefined &&
        word.word_index.toString() === wordId
    );
    
    // Check if word is NOT in active transcript
    const isNotInActive = !activeTranscript.some(
      (word) =>
        word.type === 'text' &&
        word.word_index !== undefined &&
        word.word_index.toString() === wordId
    );
    
    return isInOriginal && isNotInActive;
  };

  /**
   * Check if a collection of words can all be removed
   * In active view: if they are in the active transcript, they can be removed.
   * @param wordIds Array of word IDs to check
   * @param activeTranscript The current active transcript
   * @param viewMode The current view mode ('active', 'original', or 'full')
   * @returns Boolean indicating if all words can be removed
   */
  const canWordsBeRemoved = (
    wordIds: string[],
    activeTranscript?: TranscriptElement[],
    viewMode: 'active' | 'original' | 'full' = 'active'
  ): boolean => {
    if (!activeTranscript || wordIds.length === 0) return false;
    
    // In active view or full view, all selected words can be removed
    if (viewMode === 'active' || viewMode === 'full') {
      return true;
    }

    // In original view, check if words are in the active transcript
    return wordIds.every(id => canWordBeRemoved(id, activeTranscript, viewMode));
  };

  /**
   * Check if a collection of words can all be restored
   * In original view: if they are in the original but not in active, they can be restored.
   * In full view: if they have status='removed', they can be restored.
   * @param wordIds Array of word IDs to check
   * @param originalTranscript The original transcript
   * @param activeTranscript The current active transcript
   * @param viewMode The current view mode ('active', 'original', or 'full')
   * @returns Boolean indicating if all words can be restored
   */
  const canWordsBeRestored = (
    wordIds: string[],
    originalTranscript?: ReadonlyArray<TranscriptElement>,
    activeTranscript?: TranscriptElement[],
    viewMode: 'active' | 'original' | 'full' = 'original'
  ): boolean => {
    if (!originalTranscript || !activeTranscript || wordIds.length === 0) return false;
    
    // In original view, any selected original words not in active can be restored
    if (viewMode === 'original') {
      return wordIds.some(id => canWordBeRestored(id, originalTranscript, activeTranscript));
    }

    // In full view, check if any selected words have status='removed'
    if (viewMode === 'full') {
      // This is handled separately in the TranscriptPanel component since it needs
      // access to the inferredTranscript which isn't passed to this function
      return true;
    }

    // In active view, we typically don't need restore functionality
    return false;
  };

  return {
    isElementActive,
    canWordBeRemoved,
    canWordBeRestored,
    canWordsBeRemoved,
    canWordsBeRestored
  };
}